"use client";

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Edit,
  Eye,
  Trash2,
  X,
  Search,
  ChevronDown,
  Loader2,
  Calendar,
  CreditCard,
  Plus,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import GlobalLoader from "../components/ui/GlobalLoader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { useToast } from "../hooks/useToast";
import {
  getBookings,
  Booking,
  deleteBooking,
  getBookingById,
  updateBooking,
} from "../services/BookingsApi";
import { getEmployees } from "../services/employeesApi";
import api from "../services/api";
import BookingDetailsDrawer from "../components/BookingDetailsDrawer";
import BookingEditDrawer from "../components/BookingEditDrawer";
import AestheticBookingSidebar from "../components/aesthetics/AestheticBookingSidebar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import DateCalendar from "../components/DateCalendar";
import { format, parseISO, isValid } from "date-fns";
import { cn } from "../lib/utils";
import { useAuth } from "../auth/AuthContext";

type BookingQueryParams = {
  page?: number;
  per_page?: number;
  status?: string;
  customer_id?: number | string;
  employee_id?: number | string;
  date?: string;
  search?: string;
};

interface Customer {
  id: number;
  name: string;
}

type TabType = "All" | "Upcoming" | "Completed" | "Cancelled";

// Static demo data for UI showcase
const staticBookings = [
  {
    id: 1,
    customerName: "Sarah Miller",
    employeeName: "Emma Taylor",
    bookingTime: "May 5, 2024 10:00 AM",
    service: "Hydrafacial",
    total: 120.0,
    paid: 570.0,
    pending: 0,
    status: "Upcoming",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
  },
  {
    id: 2,
    customerName: "Emily Johnson",
    employeeName: "Dr. Saleema",
    bookingTime: "May 6, 2024 1:00 PM",
    service: "Botox Injections",
    total: 150.0,
    paid: 0,
    pending: 150.0,
    status: "Completed",
    avatar: "https://randomuser.me/api/portraits/women/2.jpg",
  },
  {
    id: 3,
    customerName: "Rachel Adams",
    employeeName: "Emma Taylor",
    bookingTime: "May 7, 2024 11:20 AM",
    service: "Lip Filler",
    total: 180.0,
    paid: 0,
    pending: 180.0,
    status: "Completed",
    avatar: "https://randomuser.me/api/portraits/women/3.jpg",
  },
  {
    id: 4,
    customerName: "Jessica Lewis",
    employeeName: "Emma Taylor",
    bookingTime: "May 8, 2024 9:00 AM",
    service: "SkinPen Microneedling",
    total: 270.0,
    paid: 0,
    pending: 270.0,
    status: "Completed",
    avatar: "https://randomuser.me/api/portraits/women/4.jpg",
  },
  {
    id: 5,
    customerName: "Megan Parker",
    employeeName: "Emma Taylor",
    bookingTime: "May 8, 2024 2:15 PM",
    service: "Chemical Peel",
    total: 100.0,
    paid: 3200.0,
    pending: 0,
    status: "Upcoming",
    avatar: "https://randomuser.me/api/portraits/women/5.jpg",
  },
  {
    id: 6,
    customerName: "Sarah Miller",
    employeeName: "Emma Taylor",
    bookingTime: "May 8, 2024 10:30 AM",
    service: "Hydrafacial - Dermal Fillers",
    total: 390.0,
    paid: 3410.0,
    pending: 0,
    status: "Cancelled",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
  },
  {
    id: 7,
    customerName: "Sarah Miller",
    employeeName: "Emma Taylor",
    bookingTime: "May 9, 2024 10:30 AM",
    service: "Laser Hair Removal",
    total: 230.0,
    paid: 2390.0,
    pending: 0,
    status: "Cancelled",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
  },
  {
    id: 8,
    customerName: "Amy Carter",
    employeeName: "Kate Morgan",
    bookingTime: "May 11, 2024 3:00 PM",
    service: "PRP Treatment",
    total: 350.0,
    paid: 2250.0,
    pending: 0,
    status: "Cancelled",
    avatar: "https://randomuser.me/api/portraits/women/6.jpg",
  },
  {
    id: 9,
    customerName: "Lisa Thempson",
    employeeName: "Emma Taylor",
    bookingTime: "May 11, 2024 9:30 AM",
    service: "Sculptra",
    total: 300.0,
    paid: 6900.0,
    pending: 0,
    status: "Cancelled",
    avatar: "https://randomuser.me/api/portraits/women/7.jpg",
  },
  {
    id: 10,
    customerName: "Laura Brown",
    employeeName: "Emma Taylor",
    bookingTime: "May 12, 2024 9:30 AM",
    service: "Sculptra",
    total: 520.0,
    paid: 5000.0,
    pending: 0,
    status: "Cancelled",
    avatar: "https://randomuser.me/api/portraits/women/8.jpg",
  },
];

export default function AestheticBookings() {
  const { user } = useAuth();
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "£";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [employees, setEmployees] = useState<{ id: number; name: string }[]>(
    []
  );
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>("All");

  // Filter states
  const [practitionerFilter, setPractitionerFilter] = useState("All Practitioners");
  const [serviceFilter, setServiceFilter] = useState("All Services");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [dateRangeFilter, setDateRangeFilter] = useState("Apr 18 - 24.4");
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("View All");
  const [dateFilter, setDateFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showPerPage, setShowPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Dropdown states
  const [isPractitionerOpen, setIsPractitionerOpen] = useState(false);
  const [isServiceOpen, setIsServiceOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isAddBookingOpen, setIsAddBookingOpen] = useState(false);

  // Drawer state
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [viewingBooking, setViewingBooking] = useState<Booking | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const toast = useToast();

  // Date filter popover state
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);

  // Global operation state for blocking UI
  const [isUpdating, setIsUpdating] = useState(false);

  // Ref to prevent duplicate API calls
  const isFetchingRef = useRef(false);
  const lastParamsRef = useRef<string>("");
  const hasInitialLoadRef = useRef(false);

  // Static services list
  const services = [
    "Hydrafacial",
    "Botox Injections",
    "Lip Filler",
    "SkinPen Microneedling",
    "Chemical Peel",
    "Dermal Fillers",
    "Laser Hair Removal",
    "PRP Treatment",
    "Sculptra",
  ];

  // Open create drawer if newBooking query param is present
  useEffect(() => {
    if (searchParams.get("newBooking") === "true") {
      setIsCreateDrawerOpen(true);
      // Clear the query param to prevent re-opening on refresh
      searchParams.delete("newBooking");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch employees for filter dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const employeesData = await getEmployees();
        console.log("Fetched employees for filter:", employeesData);
        const mappedEmployees = employeesData.map((emp) => ({
          id: emp.id,
          name: emp.name,
        }));
        console.log("Mapped employees for filter:", mappedEmployees);
        setEmployees(mappedEmployees);
      } catch (err) {
        console.error("Error fetching employees:", err);
        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // Extract customer list from varying API response shapes
  const normalizeCustomersResponse = (payload: any): Customer[] => {
    const possibleCollections = [
      payload,
      payload?.data,
      payload?.data?.data,
      payload?.data?.data?.data,
    ];

    for (const collection of possibleCollections) {
      if (Array.isArray(collection)) {
        return collection.map((customer: any) => ({
          id: customer.id,
          name: customer.name,
        }));
      }
    }

    return [];
  };

  // Fetch customers for filter dropdown
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await api.get("/admin/customers?per_page=1000&page=1");
        const result = response.data;
        const customersData = normalizeCustomersResponse(result);

        if (customersData.length === 0) {
          console.warn("Customers response structure not recognized:", result);
        }

        setCustomers(customersData);
      } catch (err) {
        console.error("Error fetching customers:", err);
        setCustomers([]);
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const buildBookingParams = useCallback(
    (overrides: Partial<BookingQueryParams> = {}) => {
      const statusParam =
        statusFilter !== "All Statuses" ? statusFilter : undefined;
      const customerParam =
        customerNameFilter && customers.length > 0
          ? customers.find((c) => c.name === customerNameFilter)?.id
          : undefined;
      const employeeParam =
        employeeFilter !== "View All" && employees.length > 0
          ? employees.find((e) => e.name === employeeFilter)?.id
          : undefined;

      return {
        page: currentPage,
        per_page: showPerPage,
        status: statusParam,
        customer_id: customerParam,
        employee_id: employeeParam,
        date: dateFilter || undefined,
        search: searchQuery || undefined,
        ...overrides,
      };
    },
    [
      statusFilter,
      customerNameFilter,
      employeeFilter,
      customers,
      employees,
      currentPage,
      showPerPage,
      dateFilter,
      searchQuery,
    ]
  );

  // Fetch bookings
  useEffect(() => {
    // Wait for customers and employees to finish loading before first fetch
    if (customersLoading || employeesLoading) {
      return;
    }

    const fetchBookingsData = async () => {
      // Prevent duplicate calls
      if (isFetchingRef.current) {
        console.log("Skipping duplicate fetch - already fetching");
        return;
      }

      const params = buildBookingParams();
      const paramsKey = JSON.stringify(params);

      // Skip if same params were just used (prevents React.StrictMode double calls)
      if (lastParamsRef.current === paramsKey && hasInitialLoadRef.current) {
        console.log("Skipping duplicate fetch - same params");
        return;
      }

      isFetchingRef.current = true;
      lastParamsRef.current = paramsKey;
      hasInitialLoadRef.current = true;

      setLoading(true);
      setError(null);
      try {
        console.log("Fetching bookings with params:", params);
        const result = await getBookings(params);
        console.log("Bookings result:", result);

        const bookingsList = result.bookings || [];
        setBookings(bookingsList);
        setTotal(result.total || bookingsList.length || 0);
        setTotalPages(result.lastPage || 1);

        if (bookingsList.length === 0) {
          setDebugInfo("");
        } else {
          setDebugInfo(
            `Loaded ${bookingsList.length} booking(s) out of ${
              result.total || 0
            } total`
          );
        }
      } catch (err: any) {
        console.error("Error fetching bookings:", err);
        console.error("Error response:", err.response?.data);
        console.error("Error stack:", err.stack);
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to load bookings. Please check the console for details.";
        setError(errorMessage);
        setBookings([]);
        setTotal(0);
        setTotalPages(1);
        toast.error("Failed to load bookings");
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchBookingsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, customerNameFilter, employeeFilter, dateFilter, searchQuery, currentPage, showPerPage, customersLoading, employeesLoading]);

  const handleReset = () => {
    setActiveTab("All");
    setPractitionerFilter("All Practitioners");
    setServiceFilter("All Services");
    setStatusFilter("All Statuses");
    setDateRangeFilter("Apr 18 - 24.4");
    setCustomerNameFilter("");
    setCustomerSearchTerm("");
    setIsCustomerDropdownOpen(false);
    setEmployeeFilter("View All");
    setDateFilter("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleEdit = async (booking: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsEditDrawerOpen(true);
    setEditLoading(true);
    setEditingBooking(booking as Booking);
    try {
      const freshBooking = await getBookingById(booking.id);
      if (freshBooking) {
        setEditingBooking(freshBooking);
      }
    } catch (err: any) {
      console.error("Error fetching booking details for edit:", err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleView = async (booking: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDetailsDrawerOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setViewingBooking(booking as Booking);
    try {
      const freshBooking = await getBookingById(booking.id);
      if (freshBooking) {
        setViewingBooking(freshBooking);
      } else {
        setDetailsError("Unable to load booking details.");
      }
    } catch (err: any) {
      console.error("Error fetching booking details:", err);
      setDetailsError(
        err.response?.data?.message || "Failed to load booking details."
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDelete = (bookingId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setBookingToDelete(bookingId);
    setIsDeleteModalOpen(true);
  };

  const handlePayment = (bookingId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    // Navigate to payment or open payment modal
    toast.success(`Opening payment for booking #${bookingId}`);
  };

  const confirmDelete = async () => {
    if (!bookingToDelete) return;

    setIsDeleting(true);
    try {
      await deleteBooking(bookingToDelete);
      const result = await getBookings(buildBookingParams());
      setBookings(result.bookings || []);
      setTotal(result.total || result.bookings?.length || 0);
      setTotalPages(result.lastPage || 1);

      if (viewingBooking?.id === bookingToDelete) {
        setViewingBooking(null);
        setIsDetailsDrawerOpen(false);
      }
      toast.success("Booking deleted successfully");
    } catch (err: any) {
      console.error("Error deleting booking:", err);
      toast.error(err.response?.data?.message || "Failed to delete booking");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setBookingToDelete(null);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm) return customers;
    const lower = customerSearchTerm.toLowerCase();
    return customers.filter((customer) =>
      customer.name.toLowerCase().includes(lower)
    );
  }, [customers, customerSearchTerm]);

  const customerDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCustomerDropdownOpen(false);
      }
    };
    if (isCustomerDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCustomerDropdownOpen]);

  // Filter static bookings based on active tab and filters
  const filteredBookings = useMemo(() => {
    let filtered = [...staticBookings];

    // Filter by tab
    if (activeTab !== "All") {
      filtered = filtered.filter((booking) => booking.status === activeTab);
    }

    // Filter by practitioner
    if (practitionerFilter !== "All Practitioners") {
      filtered = filtered.filter(
        (booking) => booking.employeeName === practitionerFilter
      );
    }

    // Filter by service
    if (serviceFilter !== "All Services") {
      filtered = filtered.filter((booking) =>
        booking.service.toLowerCase().includes(serviceFilter.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "All Statuses") {
      filtered = filtered.filter((booking) => booking.status === statusFilter);
    }

    return filtered;
  }, [activeTab, practitionerFilter, serviceFilter, statusFilter]);

  // Get unique practitioners from static data
  const practitioners = useMemo(() => {
    const unique = Array.from(new Set(staticBookings.map((b) => b.employeeName)));
    return unique;
  }, []);

  // Custom dropdown component
  const FilterDropdown = ({
    value,
    options,
    onChange,
    isOpen,
    setIsOpen,
    placeholder,
  }: {
    value: string;
    options: string[];
    onChange: (val: string) => void;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    placeholder: string;
  }) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, setIsOpen]);

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-[#885ABB] transition-colors min-w-[160px]"
        >
          <span className="truncate">{value}</span>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-gray-400 transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </button>
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={cn(
                  "w-full text-left px-4 py-2.5 text-sm hover:bg-purple-50 transition-colors",
                  value === option ? "text-[#885ABB] bg-purple-50" : "text-gray-700"
                )}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusStyles = () => {
      switch (status) {
        case "Upcoming":
          return "bg-[#885ABB] text-white";
        case "Completed":
          return "bg-[#10B981] text-white";
        case "Cancelled":
          return "bg-[#EF4444] text-white";
        default:
          return "bg-gray-100 text-gray-700";
      }
    };

    return (
      <span
        className={cn(
          "inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold",
          getStatusStyles()
        )}
      >
        {status}
      </span>
    );
  };

  return (
    <>
      {/* Global Loader for Delete/Update/Create Operations */}
      {(isDeleting || isUpdating || isCreating) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <GlobalLoader
            initialPageLoad={false}
            compensateSidebar={false}
            description={
              isDeleting
                ? "Deleting booking..."
                : isCreating
                ? "Creating booking..."
                : "Updating booking..."
            }
          />
        </div>
      )}

      <div className="w-full min-h-screen bg-[#F8F7FC] p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-[#885ABB]">Bookings</h1>
          
          {/* Add Booking Button */}
          <button
            onClick={() => setIsCreateDrawerOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-lg font-medium transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Add Booking</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 mb-6 border-b border-gray-200">
          {(["All", "Upcoming", "Completed", "Cancelled"] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
              className={cn(
                "pb-3 text-sm font-medium transition-colors relative",
                activeTab === tab
                  ? "text-[#885ABB]"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#885ABB]" />
              )}
            </button>
          ))}
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <FilterDropdown
            value={practitionerFilter}
            options={["All Practitioners", ...practitioners]}
            onChange={setPractitionerFilter}
            isOpen={isPractitionerOpen}
            setIsOpen={setIsPractitionerOpen}
            placeholder="All Practitioners"
          />

          <FilterDropdown
            value={serviceFilter}
            options={["All Services", ...services]}
            onChange={setServiceFilter}
            isOpen={isServiceOpen}
            setIsOpen={setIsServiceOpen}
            placeholder="All Services"
          />

          <FilterDropdown
            value={statusFilter}
            options={["All Statuses", "Upcoming", "Completed", "Cancelled"]}
            onChange={setStatusFilter}
            isOpen={isStatusOpen}
            setIsOpen={setIsStatusOpen}
            placeholder="All Statuses"
          />

          {/* Date Range Filter */}
          <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center justify-between gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-[#885ABB] transition-colors min-w-[140px]">
                <span>{dateRangeFilter}</span>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 text-gray-400 transition-transform",
                    isDatePopoverOpen && "rotate-180"
                  )}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <DateCalendar
                selectedDate={new Date()}
                onSelectDate={(date) => {
                  setDateRangeFilter(format(date, "MMM dd"));
                  setIsDatePopoverOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Table Header */}
              <thead>
                <tr className="bg-[#885ABB]">
                  <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                    Client
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                    Practitioner
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                    Booking Time
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                    Service
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                    Total
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-white">
                    Status
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="h-96 relative">
                      <GlobalLoader
                        initialPageLoad={false}
                        compensateSidebar={false}
                        description="Loading bookings..."
                      />
                    </td>
                  </tr>
                ) : filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-2">
                          <Calendar
                            className="w-8 h-8 text-gray-300"
                            strokeWidth={1.5}
                          />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-600">
                          No bookings found
                        </h3>
                        <p className="text-sm text-gray-400 max-w-sm mx-auto">
                          We couldn&apos;t find any bookings matching your
                          current filters.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking, index) => (
                    <tr
                      key={booking.id}
                      className={cn(
                        "border-t border-gray-100 hover:bg-purple-50/30 transition-colors",
                        index % 2 === 0 ? "bg-[#FAF9FC]" : "bg-white"
                      )}
                    >
                      {/* Client */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={booking.avatar}
                            alt={booking.customerName}
                            className="w-10 h-10 rounded-full object-cover border-2 border-purple-100"
                          />
                          <span className="font-medium text-gray-800 text-sm">
                            {booking.customerName}
                          </span>
                        </div>
                      </td>

                      {/* Practitioner */}
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {booking.employeeName}
                      </td>

                      {/* Booking Time */}
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {booking.bookingTime}
                      </td>

                      {/* Service */}
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {booking.service}
                      </td>

                      {/* Total */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-800">
                            Total - {currencySymbol}{booking.total.toFixed(2)}
                          </span>
                          <span className="text-xs text-green-600">
                            Paid: {currencySymbol}{booking.paid.toFixed(2)}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4">
                        <StatusBadge status={booking.status} />
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {/* View */}
                          <button
                            onClick={(e) => handleView(booking, e)}
                            className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Edit */}
                          <button
                            onClick={(e) => handleEdit(booking, e)}
                            className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          {/* Payment */}
                          <button
                            onClick={(e) => handlePayment(booking.id, e)}
                            className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                            title="Payment"
                          >
                            <CreditCard className="w-4 h-4" />
                          </button>

                          {/* Delete */}
                          <button
                            onClick={(e) => handleDelete(booking.id, e)}
                            className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              You can filter bookings by practitioner, service, status, or date range using the menus above.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                1 - {filteredBookings.length} of {filteredBookings.length} bookings
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={filteredBookings.length < showPerPage}
                  className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden mt-6 space-y-4">
          {filteredBookings.map((booking) => (
            <div
              key={booking.id}
              className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <img
                    src={booking.avatar}
                    alt={booking.customerName}
                    className="w-12 h-12 rounded-full object-cover border-2 border-purple-100"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {booking.customerName}
                    </h3>
                    <p className="text-sm text-gray-500">{booking.employeeName}</p>
                  </div>
                </div>
                <StatusBadge status={booking.status} />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div>
                  <span className="text-gray-500">Date:</span>
                  <p className="font-medium text-gray-800">{booking.bookingTime}</p>
                </div>
                <div>
                  <span className="text-gray-500">Service:</span>
                  <p className="font-medium text-gray-800">{booking.service}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div>
                  <span className="text-sm text-gray-800">
                    Total: {currencySymbol}{booking.total.toFixed(2)}
                  </span>
                  <span className="text-xs text-green-600 ml-2">
                    Paid: {currencySymbol}{booking.paid.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleView(booking, e)}
                    className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => handleEdit(booking, e)}
                    className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => handlePayment(booking.id, e)}
                    className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => handleDelete(booking.id, e)}
                    className="p-2 bg-[#885ABB] hover:bg-[#7248A8] text-white rounded-md transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <BookingDetailsDrawer
        isOpen={isDetailsDrawerOpen}
        booking={viewingBooking}
        loading={detailsLoading}
        error={detailsError}
        onClose={() => {
          setIsDetailsDrawerOpen(false);
          setViewingBooking(null);
        }}
        onDelete={(id: number) => handleDelete(id)}
        onEdit={(id: number) => {
          const bookingToEdit =
            bookings.find((b) => b.id === id) || viewingBooking || null;
          if (bookingToEdit) {
            handleEdit(bookingToEdit);
          }
        }}
      />

      <AestheticBookingSidebar
        isOpen={isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false);
          setEditingBooking(null);
        }}
        mode="edit"
        booking={editingBooking ? {
          id: editingBooking.id,
          clientName: editingBooking.customerName || (editingBooking as any).customerName,
          practitionerName: editingBooking.employeeName || (editingBooking as any).employeeName,
          // treatmentName: editingBooking.service || (editingBooking as any).service,
          // date: editingBooking.bookingDat,
          time: editingBooking.bookingTime,
        } : null}
        onSave={(data) => {
          console.log("Booking updated:", data);
          toast.success("Booking updated successfully");
          setIsEditDrawerOpen(false);
          setEditingBooking(null);
        }}
      />

      <AestheticBookingSidebar
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        mode="create"
        onSave={(data) => {
          console.log("New booking created:", data);
          toast.success("Booking created successfully");
          setIsCreateDrawerOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Booking"
        message="Are you sure you want to delete this booking? This action cannot be undone."
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        variant="danger"
      />
    </>
  );
}
